import { UiText } from '@experiences/ui-common';
import { InfoOutlined } from '@mui/icons-material';
import type { InputProps } from '@mui/material';
import {
    Autocomplete,
    ListItem,
    TextField,
    Tooltip,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import Tokens, { FontVariantToken } from '@uipath/apollo-core';
import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import useSWR from 'swr';
import { useDebounce } from 'use-debounce';

import { getTeamsChannels } from '../../../../services/notification-preferences';
import { accountGlobalId } from '../../../../store/selectors';
import { useTenantsContext } from '../../../tenants/TenantsContextProvider';
import type { ITeamsChannel } from '../../interfaces/notificationSettings';

const useStyles = makeStyles((theme) => ({
    teamselectinput: {
        width: '50%',
        marginTop: '10px',
    },
    teamtext: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        width: '100%',
    },
    teaminputLabel: {
        fontWeight: Tokens.FontFamily.FontBrandH4Weight,
        fontSize: Tokens.FontFamily.FontMSize,
        color: theme.palette.semantic.colorForegroundDeEmp,
        float: 'left',
    },
    teamInfoIcon: {
        marginLeft: Tokens.Padding.PadM,
        height: Tokens.FontFamily.FontXsLineHeight,
        width: Tokens.Padding.PadXxxl,
        marginTop: '3px',
    },
    teamHelptextContainer: {
        marginTop: Tokens.Padding.PadM,
        fontSize: Tokens.FontFamily.FontSSize,
        fontWeight: Tokens.FontFamily.FontWeightDefault,
        color: theme.palette.semantic.colorForegroundDeEmp,
    },
}));

interface IUiTeamsChannelPicker {
    value: ITeamsChannel;
    connectionId: string;
    teamId: string;
    searchOnly?: boolean;
    inputPlaceholder?: string;
    InputProps?: InputProps;
    form?: boolean;
    onChange?: (values: any[]) => void;
    clearErrors?: () => void;
    labelText: string;
    showTooltip: boolean;
    helpText?: string;
}

export const UiTeamsChannelPicker = ({
    value,
    searchOnly,
    onChange,
    clearErrors,
    connectionId = '',
    teamId = '',
    labelText,
    showTooltip = true,
    helpText = '',
}: IUiTeamsChannelPicker) => {
    const classes = useStyles();
    const { formatMessage: translate } = useIntl();

    const [ query, setQuery ] = useState<string>(value.displayName ?? '');
    const [ debouncedQuery ] = useDebounce(query, 500);
    const [ selected, setSelected ] = useState<ITeamsChannel[]>([ value ]);

    const [ inputValue, setInputValue ] = useState<string | undefined>(undefined);
    const accountId = useSelector(accountGlobalId);
    const { selectedTenant: { id: tenantId } } = useTenantsContext();

    const {
        data: options, isValidating: loading,
    } = useSWR(
        connectionId && debouncedQuery.trim() ? {
            SupplementConnectionId: connectionId,
            TeamId: teamId,
            startsWith: debouncedQuery,
            selectedTenantId: tenantId,
            selectedAccountId: accountId,
        } : null,
        getTeamsChannels,
    );

    return (
        <Autocomplete
            className={classes.teamselectinput}
            data-cy="Teams-channel-picker"
            includeInputInList
            options={options ?? []}
            autoComplete
            filterOptions={(x) => x}
            loading={!!loading}
            inputValue={inputValue ?? value?.displayName ?? ''}
            filterSelectedOptions
            loadingText={translate({ id: 'CLIENT_TEAMS_CHANNEL_PICKER_LOADING' })}
            getOptionLabel={(option) =>
                typeof option === 'string' ? option : option.displayName}
            value={searchOnly ? selected[0] : (([ value ]) as any[])}
            noOptionsText={query && !loading ? translate({ id: 'CLIENT_TEAMS_CHANNEL_PICKER_NO_OPTIONS' }, { query }) : ''}
            onChange={(_event, changedValue, reason) => {
                onChange?.([ changedValue ]);

                if (reason === 'removeOption' || reason === 'clear') {
                    clearErrors?.();
                }

                if (Array.isArray(changedValue)) {
                    setSelected(changedValue);
                } else {
                    setSelected([ changedValue ]);
                }

            }}
            onInputChange={(event, changedValue) => {
                if (!event) {
                    return;
                }
                setInputValue(changedValue);
                setQuery(changedValue.trim());
            }}
            renderInput={(params) => (
                <>
                    <TextField
                        {...params}
                        label={<>
                            <UiText
                                className={classes.teaminputLabel}>
                                {labelText}
                            </UiText>
                            {showTooltip && <Tooltip
                                arrow
                                title={translate({ id: 'CLIENT_NOTIFICATION_TEAMSCONFIG_CHANNEL_TOOLTIP' })}>
                                <InfoOutlined
                                    className={classes.teamInfoIcon}
                                    tabIndex={0}
                                    aria-label={translate({ id: 'CLIENT_NOTIFICATION_TEAMSCONFIG_CHANNEL_TOOLTIP' })}
                                />
                            </Tooltip>}
                        </>}
                        fullWidth />
                    {helpText && <UiText
                        className={classes.teamHelptextContainer}
                        variant={FontVariantToken.fontSizeS} >
                        {helpText}
                    </UiText> }
                </>
            )}
            renderOption={(props, option: ITeamsChannel) => (
                <ListItem
                    {...props}
                    divider
                    disableGutters>
                    <div className={classes.teamtext}>
                        <UiText>
                            {option.displayName}
                        </UiText>
                    </div>
                </ListItem>
            )}
        />

    );
};
